import { ApiHttpClient } from 'ng2-app/http/clients/api';
import { Injectable } from '@angular/core';
import { Observable, filter, of, tap } from 'rxjs';
import { FormlyField, FormlyFieldConfig } from '@ngx-formly/core';
import { FormlyModel } from 'app/form-core/formly/formly-core.config';
import { ErcContact } from 'app/models/dto/erc-contact.model';
import { DataListItem } from 'app/erc-application/erc-custom-values/erc-custom-smart-values/erc-custom-values-smart.component';
import { ContactRelationship } from 'app/models/dto/contact-relationship.model';
import { Store } from '@ngxs/store';

@Injectable({
  providedIn: 'root'
})
export class ErcService {
  relationshipsMap = {
    childOrDescendant: 'A child or a descendant of a child',
    sibling: 'A brother, sister, stepbrother, or stepsister',
    parentOrAncestor: 'The father or mother, or an ancestor of either',
    stepParent: 'A stepfather or stepmother',
    nieceOrNephew: 'A niece or nephew',
    auntOrUncle: 'An aunt or uncle',
    spouse: 'A Spouse',
    inLaw: 'A son-in-law, daughter-in-law, father-in-law, mother-in-law, brother-in-law, or sister-in-law',
    otherMemberOfHousehold: 'Other member of household'
  };

  constructor(private http: ApiHttpClient, public store: Store) {}

  getErcChecklist(borrowerId: number): Observable<any> {
    return this.http.get(`/erc-checklist-progress/${borrowerId}`);
  }

  postErcChecklist(borrowerId: number, page: string | undefined): Observable<any> {
    return this.http.post('/erc-checklist-progress/new', {'borrowerId': borrowerId, 'page': page});
  }

  putErcChecklist(checklistId: number, borrowerId: number, page: string | undefined): Observable<any> {
    return this.http.put(`/erc-checklist-progress/${checklistId}`, { 'borrowerId': borrowerId, 'page': page});
  }

  getErcLinkedBusinesses(borrowerId: number): Observable<any> {
    return this.http.get(`/borrower/${borrowerId}/linked-businesses`);
  }

  ownershipToFormlyModel(ercContacts: DataListItem[]): FormlyModel {
    const defaultModel = { ercContactFields: {} };
    return ercContacts.reduce((formlyModel: any , contact: DataListItem) => {
      formlyModel['ercContactFields'][contact.alias] = contact.value;
      return formlyModel;
    }, defaultModel);
  }

  ownershipToFormlyFieldConfigs(ercContacts: DataListItem[]): FormlyFieldConfig[] {
    const formlyFields = ercContacts.reduce((contactFields: FormlyFieldConfig[] , contact: DataListItem) => {
      if (contact.value) {
        contactFields.push({
            key: contact.alias,
            type: 'percent',
            defaultValue: contact.value,
            props: {
              min: 1,
              max: 100,
              label: contact.label,
              required: true
            }
        });
      }

      return contactFields;
    }, []);
    const fieldGroup: FormlyFieldConfig[] = [
      {
        key:'ercContactFields',
        wrappers: ['validationWrapper'],
        validators: {
          validMaxPercentage: {
            expression: (control) => {
              const keys = Object.keys(control.value);
              const totalPercentage = keys.reduce((total: number, id: string) => {
                total = total +  Number.parseInt(control.value[id]);
                return total;
              }, 0);

              if (totalPercentage <= 100) {
                return true;
              }

              return false;
            },
            message: () => {
              return 'Total owner percentages can\'t be over 100%';
            },
          }
        },
        fieldGroup: formlyFields
      }
    ];
    return fieldGroup;
  }
  formlyModelToErcContacts(ercContacts: ErcContact[], model: FormlyModel): ErcContact[] {
    return ercContacts.map((ercContact: ErcContact) => {
      if (model['ercContactFields'] && model['ercContactFields'][ercContact.contactId]) {
        ercContact.percentOwnership = model['ercContactFields'][ercContact.contactId];
      }
      return ercContact;
    });
  }

  relationshipsToDataList(relationships): DataListItem[] {
    if (relationships.length) {
      return relationships.reduce((dataListRelationships: DataListItem[], relationship: ContactRelationship) => {
        dataListRelationships.push({
          label: `${relationship.first} ${relationship.last}`,
          sectionType: 'relationships',
          value: {
            relationshipId: relationship.id,
            employee: relationship.employeeName,
            relatedToId: relationship.ownerContactId.toString(),
            contactLabel: relationship.first + ' ' + relationship.last,
            relationshipLabel: this.relationshipsMap[relationship.relationship],
            relationshipAlias: relationship.relationship,
          },
          alias: relationship.id.toString()
        });
        return dataListRelationships;
      }, []);
    } else {
      return [];
    }
  }

  ownerRelationshipsToDataList(relationships): DataListItem[] {
    if (relationships.length) {
      return relationships.reduce((dataListRelationships: DataListItem[], relationship: ContactRelationship) => {
        dataListRelationships.push({
          label: `${relationship.first} ${relationship.last}`,
          sectionType: 'relationships',
          value: {
            relationshipId: relationship.id,
            owner: relationship.ownerContactId?.toString(),
            relatedToId: relationship.relatedToContactId?.toString(),
            ownerLabel: relationship.first + ' ' + relationship.last,
            contactLabel: relationship.relatedContactsFirst + ' ' + relationship.relatedContactsLast,
            relationshipLabel: this.relationshipsMap[relationship.relationship],
            relationshipAlias: relationship.relationship,
          },
          alias: relationship.id.toString()
        });
        return dataListRelationships;
      }, []);
    } else {
      return [];
    }
  }

  relationshipToFormlyModel(relationships: DataListItem[]): FormlyModel {
    const defaultModel = {};
    if (relationships && relationships.length) {
      return relationships.reduce((formlyModel: any , relationship: DataListItem) => {
        if (!formlyModel['relationships']) {
          formlyModel['relationships'] = [];
        }
        const formlyFieldModel = {
          relationship: relationship.value.relationshipAlias,
          relatedToId: relationship.value.relatedToId,
          employee: relationship.value.employee,
          id: relationship.alias,
        };
        formlyModel['relationships'].push(formlyFieldModel);
        return formlyModel;
      }, defaultModel);
    } else {
      return defaultModel;
    }
  }

  ownerRelationshipToFormlyModel(relationships: DataListItem[]): FormlyModel {
    const defaultModel = {};
    if (relationships && relationships.length) {
      return relationships.reduce((formlyModel: any , relationship: DataListItem) => {
        if (!formlyModel['relationships']) {
          formlyModel['relationships'] = [];
        }
        const formlyFieldModel = {
          relationship: relationship.value.relationshipAlias,
          relatedToId: relationship.value.relatedToId,
          owner: relationship.value.owner,
          id: relationship.alias,
        };
        formlyModel['relationships'].push(formlyFieldModel);
        return formlyModel;
      }, defaultModel);
    } else {
      return defaultModel;
    }
  }

  relationshipToFormlyFieldConfigs(ercContacts: ErcContact[], relationshipType: string): FormlyFieldConfig[] {
    return  [{
      key: 'relationships',
      type: 'repeat-section',
      props: {
        addText: 'Add'
      },
      fieldArray: {
        className: 'erc-relationship-container',
        fieldGroup: [
          {
            key: 'employee',
            type: 'text',
            expressions: {
              hide: (field: FormlyFieldConfig) => {
                return relationshipType !== 'employee';
              }
            },
            className: 'erc-relationship-item',
            props: {
              label: 'Employee',
              required: true
            }
          },
          {
            key: 'owner',
            type: 'select',
            expressions: {
              hide: (field: FormlyFieldConfig) => {
                return relationshipType !== 'owner';
              }
            },
            className: 'erc-relationship-item',
            props: {
              options: this.convertErcContactsToOptions(ercContacts),
              label: 'Owner',
              required: true
            }
          },
          {
            key: 'relationship',
            type: 'select',
            className: 'erc-relationship-item',
            props: {
              label: 'Relationship',
              options: this.convertRelationshipMapToOptions(),
              required: true
            }
          },
          {
            key: 'relatedToId',
            type: 'select',
            className: 'erc-relationship-item',
            props: {
              options: this.convertErcContactsToOptions(ercContacts),
              label: 'To',
              required: true
            }
          },
          {
            key: 'id'
          },
          {
            key: 'deleted',
            type: 'repeat-delete',
            defaultValue: false,
          }
        ]
      }
    }];
  }
  formlyModelToRelationships(relationships: any, model: any) {
    return relationships.map((relationship: ContactRelationship) => {
      if (model['relationships'].length) {
        const employeeRelationshipData = model['relationships']
        .find((fieldData) => {
          if (fieldData.id && relationship.id.toString() === fieldData.id.toString()) {
            return fieldData;
          }
        });

        if (employeeRelationshipData) {
          relationship.employeeName = employeeRelationshipData.employee;
          relationship.ownerContactId = employeeRelationshipData.relatedToId;
          relationship.relationship = employeeRelationshipData.relationship;
        }
      }
      return relationship;
    });
  }

  formlyModelToOwnerRelationships(relationships: any, model: any) {
    return relationships.map((relationship: ContactRelationship) => {
      if (model['relationships'].length) {
        const ownerRelationshipData = model['relationships']
        .find((fieldData) => {
          if (fieldData.id && relationship.id.toString() === fieldData.id.toString()) {
            return fieldData;
          }
        });

        if (ownerRelationshipData) {
          relationship.ownerContactId = ownerRelationshipData.owner;
          relationship.relatedToContactId = ownerRelationshipData.relatedToId;
          relationship.relationship = ownerRelationshipData.relationship;
        }
      }
      return relationship;
    });
  }

  formlyModelNewToRelationships(model: any) {
      if (model['relationships'].length) {
        const employeeRelationshipData = model['relationships'].reduce((newRelationships: any, relationship) => {
          if (!relationship.id) {
            newRelationships.push({
              employeeName: relationship.employee,
              ownerContactId: relationship.relatedToId,
              relationship: relationship.relationship
            });
          }
          return newRelationships;
        }, []);
        return employeeRelationshipData.length ? employeeRelationshipData : [];
      }
  }

  formlyModelNewToOwnerRelationships(model: any) {
    if (model['relationships'].length) {
      const employeeRelationshipData = model['relationships'].reduce((newRelationships: any, relationship) => {
        if (!relationship.id) {
          newRelationships.push({
            ownerContactId: relationship.owner,
            relatedToContactId: relationship.relatedToId,
            relationship: relationship.relationship
          });
        }
        return newRelationships;
      }, []);
      return employeeRelationshipData.length ? employeeRelationshipData : [];
    }
}

  determineDeletedRelationships(model: any, relationship) {
    return relationship.id && !model['relationships'].find(modelData => {
      if (modelData.id && modelData.id.toString() === relationship.id.toString()) {
        return modelData;
      }
    });
  }

  formlyRetrieveNewRelationships(model: any) {
    return model.filter((relationship) => !relationship.id);
  }

  convertErcContactsToOptions(ercContacts) {
    return ercContacts.reduce((ercContactOptions: {label: string, value: string}[], ercContact: ErcContact) => {
      const option = {
        label: ercContact.first + ' ' + ercContact.last,
        value: ercContact.contactId.toString()
      };
      ercContactOptions.push(option);
      return ercContactOptions;
    }, []);
  }

  convertRelationshipMapToOptions() {
    return Object.keys(this.relationshipsMap).reduce((options: {label: string, value: string}[], key: string) => {
      const option = {
        label: this.relationshipsMap[key],
        value: key
      };
      options.push(option);
      return options;
    }, []);

  }

}
